/**
 * Short-lived access tokens for provider fetches.
 *
 * The durable half of the grant -- the refresh token -- lives on the server and
 * never comes here. This module holds only the hourly access token, and holds
 * it *in memory*: a copy in localStorage would survive exactly as long as it
 * took someone to read it out, and would buy nothing, because the server can
 * always mint another.
 */

import { API_URL } from "@/common/constants";
import { HttpError, throwIfNotOk } from "@/api/errors";
import { ExternalAuthError } from "./types";

interface CachedToken {
  token: string;
  /** Epoch millis. */
  expires: number;
}

/** Re-mint this long before expiry, so a fetch starting now cannot outlive it. */
const RENEW_MARGIN_MS = 60_000;

const cache = new Map<string, CachedToken>();
// One request per connection at a time. A collection and its five
// subcollections all load at once, and without this they would each ask the
// server for the same token.
const inFlight = new Map<string, Promise<string>>();

export function forgetToken(connectionId: string) {
  cache.delete(connectionId);
  inFlight.delete(connectionId);
}

export function forgetAllTokens() {
  cache.clear();
  inFlight.clear();
}

async function mint(connectionId: string): Promise<string> {
  const response = await fetch(
    `${API_URL}/connections/${encodeURIComponent(connectionId)}/token`,
    { method: "POST", credentials: "include" },
  );

  try {
    await throwIfNotOk(response);
  } catch (e) {
    // The session is fine; it is the grant on the far side that is gone.
    if (e instanceof HttpError && e.status === 409) {
      throw new ExternalAuthError(e.message);
    }
    throw e;
  }

  const data = (await response.json()) as {
    access_token: string;
    expires_at: string;
  };
  cache.set(connectionId, {
    token: data.access_token,
    expires: new Date(data.expires_at).getTime(),
  });
  return data.access_token;
}

export async function getAccessToken(connectionId: string): Promise<string> {
  const cached = cache.get(connectionId);
  if (cached && cached.expires - RENEW_MARGIN_MS > Date.now()) {
    return cached.token;
  }

  const existing = inFlight.get(connectionId);
  if (existing) return existing;

  // Deliberately not passing the caller's AbortSignal: several sources share
  // this promise, so one of them navigating away must not cancel the token the
  // others are waiting on. The request is small and the result is cached.
  const request = mint(connectionId).finally(() => {
    inFlight.delete(connectionId);
  });
  inFlight.set(connectionId, request);
  return request;
}
